// @flow
import React, { useMemo, useState, useEffect } from 'react';
import {
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import { DASHES } from '@fortress-technology-solutions/fortress-component-library/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useHistory from '../../hooks/useHistory';
import messages from './messages';
import regularAssistancePaymentsMessages from './RegularAssistancePayments/messages';
import useIntl from '../../hooks/useIntl';
import useHUDVoucherStatsQuery from './hooks.useHUDVoucherStatsQuery';

export const TAB_IDS = {
  NEW_TENANT_EVENTS: 'new-tenant-events',
  REGULAR_ASSISTANCE_PAYMENTS: 'regular-assistance-payments',
  SPECIAL_CLAIMS: 'special-claims',
  REPAYMENT_AGREEMENTS: 'repayment-agr',
  MISCELLANEOUS_ACCOUNTING: 'misc-acct',
  REVIEW_SUBMIT_VOUCHER: 'review-submit-voucher',
};

interface BucketStats {
  unitCount?: number;
  requestedAmount?: number;
  approvedAmount?: number;
  caHudApprovedAmount?: number;
  varianceAmount?: number;
}

interface Props {
  hudVoucherId: string;
  organizationId: string;
  propertyId: string;
}

const SummaryStat = ({
  label,
  value,
  isCurrency = false,
  isVariance = false,
}: {
  label: string,
  value: ?number,
  isCurrency?: boolean,
  isVariance?: boolean,
}) => {
  const hasNumericValue = typeof value === 'number' && Number.isFinite(value);
  const normalizedValue = hasNumericValue ? value : 0;
  const hasVariance = isVariance && normalizedValue !== 0;
  let valueContent = DASHES;
  if (hasNumericValue) {
    valueContent = isCurrency
      ? formatCurrency(normalizedValue)
      : normalizedValue;
  }

  return (
    <Typography
      variant="bodySmall"
      color={hasVariance ? 'error' : 'text.secondary'}
    >
      {label}: {valueContent}
    </Typography>
  );
};

const BucketSummary = ({
  stats,
  showUnits = false,
  intl,
}: {
  stats: ?BucketStats,
  showUnits?: boolean,
  intl: any,
}) => {
  return (
    <Stack>
      {showUnits && (
        <SummaryStat
          label={intl.formatMessage(messages.unitsLabel)}
          value={stats?.unitCount}
        />
      )}
      <SummaryStat
        label={intl.formatMessage(messages.requestedLabel)}
        value={stats?.requestedAmount}
        isCurrency
      />
      <SummaryStat
        label={intl.formatMessage(messages.approvedLabel)}
        value={stats?.approvedAmount}
        isCurrency
      />
      <SummaryStat
        label={intl.formatMessage(messages.caHudApprovedLabel)}
        value={stats?.caHudApprovedAmount}
        isCurrency
      />
      <SummaryStat
        label={intl.formatMessage(messages.varianceLabel)}
        value={stats?.varianceAmount}
        isCurrency
        isVariance
      />
    </Stack>
  );
};

export const useManageHudVoucherTabs = ({
  hudVoucherId,
  organizationId,
  propertyId,
}: Props): Object => {
  const intl = useIntl();
  const { hudTenantEvents, hudManageVouchers, hudTracsIntegration } =
    useFlags();

  const { data: voucherStats, isSuccess: voucherStatsIsSuccess } =
    useHUDVoucherStatsQuery({
      hudVoucherId,
      organizationId,
      propertyId,
    });

  const tenantEventsStatusCounts = useMemo(() => {
    return {
      notSubmitted: voucherStats?.newTenantEvents?.notSubmittedCount ?? 0,
      submitted: voucherStats?.newTenantEvents?.submittedCount ?? 0,
    };
  }, [
    voucherStats?.newTenantEvents?.notSubmittedCount,
    voucherStats?.newTenantEvents?.submittedCount,
  ]);

  const isVoucherEmpty = useMemo(() => {
    if (!voucherStatsIsSuccess) return false;

    const hasNewTenantEvents =
      (voucherStats?.newTenantEvents?.notSubmittedCount ?? 0) +
        (voucherStats?.newTenantEvents?.submittedCount ?? 0) >
      0;
    const hasRegularAssistance =
      (voucherStats?.regularAssistance?.unitCount ?? 0) > 0;
    const hasAdjustments = (voucherStats?.adjustments?.unitCount ?? 0) > 0;
    const hasSpecialClaims = (voucherStats?.specialClaims?.unitCount ?? 0) > 0;
    const hasRepaymentAgreements =
      (voucherStats?.repaymentAgreements?.requestedAmount ?? 0) > 0;
    const hasMiscAccounting =
      (voucherStats?.miscellaneousAccounting?.requestedAmount ?? 0) > 0;
    return (
      !hasNewTenantEvents &&
      !hasRegularAssistance &&
      !hasAdjustments &&
      !hasSpecialClaims &&
      !hasRepaymentAgreements &&
      !hasMiscAccounting
    );
  }, [
    voucherStatsIsSuccess,
    voucherStats?.newTenantEvents?.notSubmittedCount,
    voucherStats?.newTenantEvents?.submittedCount,
    voucherStats?.regularAssistance?.unitCount,
    voucherStats?.adjustments?.unitCount,
    voucherStats?.specialClaims?.unitCount,
    voucherStats?.repaymentAgreements?.requestedAmount,
    voucherStats?.miscellaneousAccounting?.requestedAmount,
  ]);

  const tabs = useMemo(() => {
    const tabConfigs = [
      {
        condition: hudTenantEvents,
        tab: {
          id: 'new-tenant-events',
          title: 'New Tenant Events',
          content: (
            <Stack>
              <Typography variant="bodySmall" color="text.secondary">
                {parseCount(
                  tenantEventsStatusCounts.notSubmitted,
                  'Not Submitted',
                  'Not Submitted',
                )}
              </Typography>
              <Typography variant="bodySmall" color="text.secondary">
                {parseCount(
                  tenantEventsStatusCounts.submitted,
                  'Submitted',
                  'Submitted',
                )}
              </Typography>
            </Stack>
          ),
        },
      },
      {
        condition: true,
        tab: {
          id: TAB_IDS.REGULAR_ASSISTANCE_PAYMENTS,
          title: intl.formatMessage(
            regularAssistancePaymentsMessages.regularAssistancePayments,
          ),
          content: (
            <BucketSummary
              stats={voucherStats?.regularAssistance}
              showUnits={true}
              intl={intl}
            />
          ),
        },
      },
      {
        condition: hudManageVouchers,
        tab: {
          id: 'adjustments',
          title: intl.formatMessage(messages.adjustments),
          content: (
            <BucketSummary
              stats={voucherStats?.adjustments}
              showUnits={true}
              intl={intl}
            />
          ),
        },
      },
      {
        condition: true,
        tab: {
          id: 'misc-acct',
          title: 'Miscellaneous Accounting',
          content: (
            <BucketSummary
              stats={voucherStats?.miscellaneousAccounting}
              intl={intl}
            />
          ),
        },
      },
      {
        condition: true,
        tab: {
          id: 'repayment-agr',
          title: 'Repayment Agreement',
          content: (
            <BucketSummary
              stats={voucherStats?.repaymentAgreements}
              intl={intl}
            />
          ),
        },
      },
      {
        condition: true,
        tab: {
          id: 'special-claims',
          title: 'Special Claims',
          content: (
            <BucketSummary
              stats={voucherStats?.specialClaims}
              showUnits={true}
              intl={intl}
            />
          ),
        },
      },
      {
        condition: hudTracsIntegration,
        tab: {
          id: 'review-submit-voucher',
          title: 'Review & Submit Voucher',
          content: (
            <Stack alignItems="flex-start">
              <SummaryStat
                label={intl.formatMessage(messages.totalRequestedLabel)}
                value={
                  voucherStats?.reviewAndSubmitVoucher?.totalRequestedAmount
                }
                isCurrency
              />
              <SummaryStat
                label={intl.formatMessage(messages.totalCaHudApprovedLabel)}
                value={
                  voucherStats?.reviewAndSubmitVoucher?.totalCaHudApprovedAmount
                }
                isCurrency
              />
            </Stack>
          ),
        },
      },
    ];

    return tabConfigs
      .filter(({ condition }) => condition)
      .map(({ tab }) => tab);
  }, [
    hudTenantEvents,
    tenantEventsStatusCounts.notSubmitted,
    tenantEventsStatusCounts.submitted,
    intl,
    voucherStats?.regularAssistance,
    voucherStats?.adjustments,
    voucherStats?.miscellaneousAccounting,
    voucherStats?.repaymentAgreements,
    voucherStats?.specialClaims,
    voucherStats?.reviewAndSubmitVoucher,
    hudManageVouchers,
    hudTracsIntegration,
  ]);

  const [tab, setTab] = useTabSelection(tabs);

  return { tab, setTab, tabs, isVoucherEmpty };
};

export const useTabSelection = (
  tabs: Array<any>,
): [string | void, (newTab: string) => void] => {
  const history = useHistory();

  // Get tab from query parameter or default to first tab
  const getTabFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(history.location.search);
    const tabFromUrl = searchParams.get('tab');
    const validTab = tabs.find((t) => t.id === tabFromUrl);
    return validTab ? validTab.id : tabs[0]?.id;
  }, [history.location.search, tabs]);

  const [tab, setTab] = useState(getTabFromUrl);

  // Sync tab state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlTab = getTabFromUrl;
    if (urlTab !== tab) {
      setTab(urlTab);
    }
  }, [getTabFromUrl, tab]);

  // Update URL when tab changes
  const handleTabChange = (newTab: string) => {
    setTab(newTab);
    const searchParams = new URLSearchParams(history.location.search);
    searchParams.set('tab', newTab);
    const newSearch = searchParams.toString();
    const newPath = newSearch
      ? `${history.location.pathname}?${newSearch}`
      : history.location.pathname;
    history.replace(newPath);
  };

  return [tab, handleTabChange];
};

function parseCount(
  count: number = 0,
  plural: string,
  singular: string,
): string {
  if (count !== 1) {
    return `${count} ${plural}`;
  }
  return `${count} ${singular}`;
}
